import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import CTA, { CTAProps } from 'Src/components/buttons/CTA';
import { UsedCheckPanelFooter } from 'Src/feature/check/useCheckPanelFooter';
import { CheckPaymentPreAuthCTA } from 'Src/feature/payment/CheckPaymentPreAuthCTA';
import { useTerminal } from 'Src/feature/terminal/useTerminal';
import useRoute from 'Src/routes/useRoute';

const sectionId = 'CheckPanelFooterMainCTA';

interface CheckPanelFooterMainActionProps {
  store: UsedCheckPanelFooter;
}

const CheckPanelFooterMainCTA: FC<CheckPanelFooterMainActionProps> = ({
  store,
}) => {
  const { t } = useTranslation();
  const { crumbs, route } = useRoute();
  const { described: { check, has, payment } } = store;
  const terminalSettings = useTerminal.query.settings();

  // const checkPrints = useCheckPrints({ checkId: currentCheck.query.data?.id });
  const is = {
    payment: {
      route: route.is(crumbs.pos.check.payment.main),
    },
  };

  const buttonProps: CTAProps = {
    color: 'primary',
    disabled: false,
    fullWidth: true,
    icon: undefined,
    label: '',
    onClick: undefined,
    pill: true,
    size: 'lg',
  };

  // if we don't have items or gift cards
  if (!has.check.items.active && !has.giftCards) {
    buttonProps.disabled = true;
    buttonProps.icon = { name: 'send', position: 'start' };
    buttonProps.onClick = undefined;
    switch (terminalSettings.data?.sendItemsAction) {
      case 'Back':
        buttonProps.label = t('labels.sendAndBack');
        break;
      case 'Logout':
        buttonProps.label = t('labels.sendAndLogout');
        break;
      case 'Stay':
        buttonProps.label = t('labels.send');
        break;
      default:
        buttonProps.label = t('labels.send');
        break;
    }
    return <CTA {...buttonProps} />;
  }

  // If the check has no items, but DO have gift cards, and we are NOT on the payment page
  if (!has.check.items.active && !is.payment.route && has.giftCards) {
    if (has.check.balance.zero) {
      buttonProps.disabled = false;
      buttonProps.label = t('labels.closeCheck');
      buttonProps.onClick = store.closeCheck;
    } else {
      buttonProps.disabled = false;
      buttonProps.icon = { name: 'payment', position: 'start' };
      buttonProps.label = t('labels.pay');
      buttonProps.onClick = store.onPayClick;
    }
    return <CTA {...buttonProps} />;
  }

  // If the check contains unsent items
  if (has.check.items.unsent) {
    buttonProps.disabled = store.isSendingItems;
    buttonProps.icon = { name: 'send', position: 'start' };
    buttonProps.onClick = store.onSendItemsClick;
    buttonProps.disabled = check?.status === 'Scheduled';
    switch (terminalSettings.data?.sendItemsAction) {
      case 'Back':
        buttonProps.label = t('labels.sendAndBack');
        break;
      case 'Logout':
        buttonProps.label = t('labels.sendAndLogout');
        break;
      case 'Stay':
        buttonProps.label = t('labels.send');
        break;
      default:
        buttonProps.label = t('labels.send');
        break;
    }
    return <CTA {...buttonProps} />;
  }

  // TODO: when the check prints is sorted after launch
  //  - we can pull this back in
  //  - going to show loading states on each check item button as that is non-blocking
  // let hasPendingPrintJobs = false;
  // for (const checkItem of checkItems) {
  //   const printJob = checkPrints.all.map.get(checkItem.id);
  //   const printState = printJob?.states.combined.state;
  //   const isHolding = printState === 'Scheduled' && printJob?.print.dateTime && printJob?.print.dateTime?.toMillis() > DateTime.now().plus({ seconds: 5 }).toMillis();
  //   // ignore check prints that are on hold
  //   if (!isHolding) {
  //     if (printState === 'Scheduled' || printState === 'Progress') {
  //       hasPendingPrintJobs = true;
  //       break;
  //     }
  //   }
  // }
  // // If the check contains **NO** unsent items
  // // AND If Print Jobs are pending
  // if (hasPendingPrintJobs) {
  //   buttonProps.disabled = true;
  //   buttonProps.icon = { name: 'send', position: 'start' };
  //   buttonProps.label = t('labels.sending');
  //   buttonProps.onClick = undefined;
  //   return <CTA {...buttonProps} />;
  // }

  // If the check contains **NO** unsent items
  // AND If **NO** Print Jobs are pending
  // AND If an itemized check receipt has NOT yet been printed,
  // TODO: need to be able to determine if a guest receipt has been made
  //  - https://nabancard.atlassian.net/browse/HOSPENG-9589
  const checkHasPrintedItemizedReceipt = false;
  if (checkHasPrintedItemizedReceipt) {
    buttonProps.disabled = false;
    buttonProps.icon = { name: 'print', position: 'start' };
    buttonProps.label = t('labels.printReceipt');
    buttonProps.onClick = () => console.info('TODO: print guest receipt');
    return <CTA {...buttonProps} />;
  }

  // If the check contains **NO** unsent items
  // AND If **NO** Print Jobs are pending
  // AND If an itemized check receipt has been printed,

  // NOT a bar tab payment AND has zero balance
  if (!payment.startTabWithCard && has.check.balance.zero) {
    // disable the ability to close check when the balance is not zero or when items are still in scheduled or held state
    // TODO: resolve copy/pasta from before...
    //  ?? how could we have a zero balance and also balance.unpaid !== 0
    buttonProps.disabled = check?.balance.unpaid !== 0 || check?.items.some((i) => (i.sendSchedule?.sendAt && i.status === 'Added') || i.status === 'IndefiniteHold');
    buttonProps.label = t('labels.closeCheck');
    buttonProps.onClick = store.closeCheck;
    return <CTA {...buttonProps} />;
  }

  // AND is a payment route
  if (is.payment.route) {

    // AND has zero balance
    if (has.check.balance.zero) {
      // disable the ability to close check when the balance is not zero or when items are still in scheduled or held state
      // TODO: resolve copy/pasta from before...
      //  ?? how could we have a zero balance and also balance.unpaid !== 0
      buttonProps.disabled = check?.balance.unpaid !== 0 || check?.items.some((i) => (i.sendSchedule?.sendAt && i.status === 'Added') || i.status === 'IndefiniteHold');
      buttonProps.label = t('labels.closeCheck');
      buttonProps.onClick = store.closeCheck;
      return <CTA {...buttonProps} />;
    }

    // AND has a balance
    // AND is a bar tab payment
    if (payment.startTabWithCard) {
      buttonProps.label = t('labels.payTab');
      // click is handled inside of "CheckPaymentPreAuthCTA"
      buttonProps.onClick = undefined;
      // pull in same props to match the styles
      return <CheckPaymentPreAuthCTA {...buttonProps} payment={payment.startTabWithCard} />;
    }
  }

  // If the check contains **NO** unsent items
  // AND If **NO** Print Jobs are pending
  // AND If an itemized check receipt has been printed,
  // AND NOT a payment route
  // OR is a bar tab payment
  // AND some balance exists
  // COULD be a bar tab
  // ALLOW PAYMENT
  buttonProps.disabled = false;
  buttonProps.icon = { name: 'payment', position: 'start' };
  buttonProps.label = t('labels.pay');
  buttonProps.onClick = store.onPayClick;
  return <CTA {...buttonProps} />;

};

export type { CheckPanelFooterMainActionProps };
export { CheckPanelFooterMainCTA, sectionId };
